import ChevronLeftSvg from "@/src/assets/icons/icon-chevron-left-black.svg";
import ClockSvg from "@/src/assets/icons/icon-clock-black.svg";
import InfoGray6Svg from "@/src/assets/icons/icon-info-gray6.svg";
import InfoSvg from "@/src/assets/icons/icon-info.svg";
import AlertDialog from "@/src/components/atom/AlertDialog";
import { Button, TextButton } from "@/src/components/atom/Button";
import Checkbox from "@/src/components/atom/Checkbox";
import Icon from "@/src/components/atom/Icon";
import Input from "@/src/components/atom/Input";
import NumberInput from "@/src/components/atom/Input/NumberInput";
import Label from "@/src/components/atom/Label";
import Loader from "@/src/components/atom/Loader";
import Radio from "@/src/components/atom/Radio";
import RadioGroup from "@/src/components/atom/RadioGroup";
import Select from "@/src/components/atom/Select";
import Typo from "@/src/components/atom/Typo";
import BottomFixedContainer from "@/src/components/molecule/BottomFixedContainer";
import EtdCompareCount from "@/src/components/molecule/EtdCompareCount";
import Flex from "@/src/components/molecule/Flex";
import FormItem, { InputError } from "@/src/components/molecule/FormItem";
import SectionCard from "@/src/components/molecule/SectionCard";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import UnauthorizedDescription from "@/src/components/molecule/UnauthorizedDescription";
import PhotoTypeFields from "@/src/components/organism/PhotoTypeFields";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import { NOT_FOUND_CODE } from "@/src/constant/error";
import useAlert from "@/src/hooks/useAlert";
import useContentLoading from "@/src/hooks/useContentLoading";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useGetSessionQuery } from "@/src/store/apis/auth";
import { useLazyGetBuyerQuery } from "@/src/store/apis/client/buyer";
import {
  useGetFactoriesQuery,
  useLazyGetFactoryEmployeesQuery,
} from "@/src/store/apis/client/factory";
import { useLazyGetContractDetailQuery } from "@/src/store/apis/contracts/contractDetail";
import { useLazyGetContractListAssociatedWithBookingQuery } from "@/src/store/apis/contracts/contractList";
import { BookingContractListViewDto } from "@/src/store/apis/contracts/contractList/interface";
import { useLazyGetItemCodeListQuery } from "@/src/store/apis/corporate/companyItemManagement";
import { ExporterItemCodeListViewV2Dto } from "@/src/store/apis/corporate/companyItemManagement/interface";
import { useLazyGetTaskDetailQuery } from "@/src/store/apis/tasks/taskDetail";
import { useEditTaskMutation } from "@/src/store/apis/tasks/taskEdit";
import { ModifyTaskDto } from "@/src/store/apis/tasks/taskEdit/interface";
import { useLazyGetBookingListAssociatedWithTaskQuery } from "@/src/store/apis/tasks/taskRegister";
import {
  MediaCoordinateRangeType,
  TaskBookingListViewDto,
  TaskPhotoTypeRequestItemDto,
  TaskPhotoTypeRequestResponseDto,
} from "@/src/store/apis/tasks/taskRegister/interface";
import { useGetPhotoTypeConfigQuery } from "@/src/store/apis/photoType";
import type { PhotoTypeCode } from "@/src/store/apis/photoType/interface";
import type { PhotoTypeFormItem } from "./types";
import { ErrorResponse } from "@/src/store/apis/type";
import colorSet from "@/src/styles/color";
import { mediaQuery } from "@/src/styles/mediaQuery";
import typo from "@/src/styles/typography";
import { isNull, isUndefined } from "@/src/utils/is";
import { transformValueOrNull } from "@/src/utils/transform";
import { Radio as AntdRadio } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { css, styled } from "styled-components";
import AddNewContactPersonDialog from "./components/add/dialog/AddNewContactPersonDialog";
import LoadBookingDialog from "./components/add/dialog/LoadBookingDialog";
import LoadContractDialog from "./components/add/dialog/LoadContractDialog";
import {
  getMaxContainerInfoViaLang,
  MEDIA_COORDINATE_RANGE_OPTION_LIST,
} from "./constant/add";
import { editTaskPageDefaultValue } from "./utils/defaultValues";

const cardContentContainerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
};

enum DialogState {
  NULL,
  BOOKING,
  CONTRACT,
  ADD_NEW_CONTACT_PERSON,
}

enum AlertDialogState {
  NULL,
  SAVED_DATA,
  BACK_TO_PREVIOUS,
}

const photoTypeMap = {
  containerBothDoorsClosedMediasCount: "bothDoorsClosed",
  containerCloseUpSealMediasCount: "closeUpSeal",
  containerEmptyMediasCount: "empty",
  containerExtraMediasCount: "extra",
  containerFullLoadedMediasCount: "fullLoaded",
  containerHalfLoadedMediasCount: "halfLoaded",
  containerOneDoorClosedMediasCount: "oneDoorClosed",
  containerQuarterLoadedMediasCount: "quarterLoaded",
  containerSampleBaleMediasCount: "sampleBale",
};

type PhotoTypeContainerKey =
  | "containerBothDoorsClosedMediasCount"
  | "containerCloseUpSealMediasCount"
  | "containerEmptyMediasCount"
  | "containerExtraMediasCount"
  | "containerFullLoadedMediasCount"
  | "containerHalfLoadedMediasCount"
  | "containerOneDoorClosedMediasCount"
  | "containerQuarterLoadedMediasCount"
  | "containerSampleBaleMediasCount";

const zeroOrPositiveNumber = (bool: boolean, value: number) => {
  return !bool ? 0 : value;
};

const emptyArray: [] = [];
const itemEmptyArray: ExporterItemCodeListViewV2Dto[] = [];

const TaskEditPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const alert = useAlert();
  const { handleContentLoadingOff, handleContentLoadingOn } =
    useContentLoading();
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);
  const [alertDialogState, setAlertDialogState] = useState<AlertDialogState>(
    AlertDialogState.NULL
  );
  const [isSameAsWorkingDay, setIsSameAsWorkingDay] = useState<boolean>(false);
  const [excludedManagerId, setExcludedManagerId] = useState<
    (string | undefined)[]
  >([]);
  const [bookingDetail, setBookingDetail] =
    useState<TaskBookingListViewDto | null>(null);
  const [contractDetail, setContractDetail] =
    useState<BookingContractListViewDto | null>(null);
  const [loadedPhotoTypeRequests, setLoadedPhotoTypeRequests] = useState<
    TaskPhotoTypeRequestResponseDto[] | null
  >(null);
  // Task 데이터 로딩 완료 플래그
  const [isTaskDataLoaded, setIsTaskDataLoaded] = useState<boolean>(false);

  const { mainCategoryList, exporterUserType } = useGetSessionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      const isManager =
        currentData?.row.exporterUserType === "MANAGER" ||
        currentData?.row.exporterUserType === "MIDDLE_MANAGER";

      return {
        mainCategoryList: isStable
          ? isManager
            ? currentData.row.mainCategoryCodeItemNames
            : currentData.row.exporter.mainCategoryCodeItemNames
          : emptyArray,
        exporterUserType: currentData?.row.exporterUserType,
      };
    },
  });
  const { factoryList, isFetching } = useGetFactoriesQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ currentData, isFetching, isError }) => {
        const isUnstable = isUndefined(currentData) || isFetching || isError;

        return {
          factoryList: !isUnstable ? currentData.rows : emptyArray,
          isFetching,
        };
      },
    }
  );
  const [
    getFactoryEmployees,
    { factoryEmployees, isFactoryEmployeesFetching },
  ] = useLazyGetFactoryEmployeesQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isUndefined(currentData) || isFetching || isError;

      return {
        factoryEmployees: !isUnstable ? currentData.rows : emptyArray,
        isFactoryEmployeesFetching: isFetching,
      };
    },
  });

  // Photo Type 설정 조회 (회사별 커스텀 사진 타입)
  const { data: photoTypeConfig, isLoading: isPhotoTypeConfigLoading } =
    useGetPhotoTypeConfigQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const [editLoading] = useEditTaskMutation();
  const [getBookingList] = useLazyGetBookingListAssociatedWithTaskQuery();
  const [getContractList] = useLazyGetContractListAssociatedWithBookingQuery();
  const [
    getLoadingDetail,
    { workplaceId, isEditAuth, isLoadingFetchingError, isLoadingUnstable },
  ] = useLazyGetTaskDetailQuery({
    selectFromResult: ({ currentData, isFetching, isError, error }) => {
      const isUnstable = isUndefined(currentData) || isFetching || isError;
      const isStable = !isUnstable;
      const isNotFoundError =
        !!error && (error as ErrorResponse).data.statusCode === NOT_FOUND_CODE;

      return {
        isLoadingUnstable: isUnstable,
        workplaceId: isStable ? currentData.workplaceId : 0,
        isEditAuth: !isNotFoundError,
        isLoadingFetchingError: isError,
        isLoadingFetching: isFetching,
      };
    },
  });
  const [getBuyerDetail] = useLazyGetBuyerQuery({
    selectFromResult: ({ currentData, isFetching, isError }) => {
      const isUnstable = isUndefined(currentData) || isFetching || isError;
      const isStable = !isUnstable;
      return {
        mainCategoryCodeItem: isStable
          ? currentData.mainCategoryCodeItemNames
          : emptyArray,
      };
    },
  });

  const [getItemCodes, { itemList, isItemCodeUnstable }] =
    useLazyGetItemCodeListQuery({
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);

        return {
          isItemCodeUnstable: isUnstable,
          itemList: !isUnstable
            ? (currentData?.rows ?? itemEmptyArray)
            : itemEmptyArray,
        };
      },
    });

  const [
    getContractDetail,
    { contractDetailMainCategory, isContractDetailFetching },
  ] = useLazyGetContractDetailQuery({
    selectFromResult: ({ currentData, isFetching }) => {
      return {
        contractDetailMainCategory: currentData?.mainCategory,
        isContractDetailFetching: isFetching,
      };
    },
  });

  const mainCategoryItemName = itemList.find(
    (item) => item.itemCode === contractDetail?.itemCode
  )?.mainCategoryCodeItemName;

  // 매니저,중간관리자 카테고리 비교
  const isMainCategoryAuthorized =
    exporterUserType === "MANAGER" || exporterUserType === "MIDDLE_MANAGER"
      ? mainCategoryList.includes(mainCategoryItemName as string)
      : true;
  const isMainCategoryUnauthorized =
    !isMainCategoryAuthorized || isLoadingFetchingError;

  const {
    control,
    watch,
    setValue,
    clearErrors,
    handleSubmit,
    getValues,
    reset,
    formState: { errors, isSubmitted },
  } = useForm<{
    bookingId?: number;
    contractId?: number;
    workingDayAt?: string;
    transportDateAt?: string;
    transportRemark?: string;
    workEstimatedTime?: Dayjs;
    containerTaskRequestRemark?: string;
    /**
     * @deprecated Legacy photo type fields - API 호환성을 위해 유지
     * UI에서는 photoTypeItems 배열을 사용하고, submit 시 이 필드들로 변환됨
     * 새 코드에서는 photoTypeItems를 사용할 것
     */
    sampleBale?: { count: number; isUse: boolean };
    empty?: { count: number; isUse: boolean };
    quarterLoaded?: { count: number; isUse: boolean };
    halfLoaded?: { count: number; isUse: boolean };
    fullLoaded?: { count: number; isUse: boolean };
    oneDoorClosed?: { count: number; isUse: boolean };
    bothDoorsClosed?: { count: number; isUse: boolean };
    /** 고정 항목: Close-up Seal (항상 필수) */
    closeUpSeal?: { count: number; isUse: boolean };
    /** 고정 항목: Extra (선택적 사용) */
    extra?: { count: number; isUse: boolean; remark?: string };
    workplaceId?: string;
    numberOfTaskContainer: number;
    taskCode: string;
    taskWorkplaceEmployeeInfos: {
      transmissionMethod?: string;
      workplaceEmployeeId?: string;
    }[];
    mediaCoordinateRange: MediaCoordinateRangeType;
    // 동적 photo type 시스템 (API에서 로드)
    photoTypeItems: PhotoTypeFormItem[];
  }>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      ...editTaskPageDefaultValue,
      photoTypeItems: [],
    },
  });

  // 고정 photo type (Close-up Seal, Extra) watch - 컴포넌트 레벨
  const watchedCloseUpSeal = watch("closeUpSeal");
  const watchedExtra = watch("extra");

  // 계약의 메인 카테고리에 따른 필터링
  const factoriesToOptionList = factoryList
    .filter(
      ({ isActivated, workplaceListQueryResultDto, mainCategory }) =>
        (isActivated &&
          mainCategory
            .split(", ")
            .some((category) => category === contractDetailMainCategory)) ||
        workplaceId.toString() === workplaceListQueryResultDto.id.toString()
    )
    .reduce<{ label: string; value: string }[]>((acc, val) => {
      const resource = {
        label: val.workplaceName,
        value: val.workplaceListQueryResultDto.id.toString(),
      };

      return [...acc, resource];
    }, []);

  const factoryEmployeesToOptionList = factoryEmployees
    .filter(
      ({ isActivated, id }) =>
        isActivated || excludedManagerId.includes(id.toString())
    )
    .reduce<{ label: string; value: string }[]>((acc, val) => {
      const resource = {
        label: `${val.name} / ${val.personalContactPrefix} ${val.personalContact} / ${val.email}`,
        value: val.id.toString(),
      };

      return [...acc, resource];
    }, []);

  const handleEditLoading = async () => {
    const {
      photoTypeItems,
      workingDayAt,
      transportDateAt,
      transportRemark,
      containerTaskRequestRemark,
      bookingId,
      contractId,
      numberOfTaskContainer,
      workplaceId,
      taskWorkplaceEmployeeInfos,
      workEstimatedTime,
      mediaCoordinateRange,
    } = getValues();

    // photoTypeItems에서 typeCode로 항목 찾기 헬퍼
    const getPhotoTypeByCode = (code: PhotoTypeCode) =>
      photoTypeItems.find((item) => item.typeCode === code);

    // 레거시 필드로 이미 전송되는 기본 타입 코드 (중복 전송 방지)
    const LEGACY_TYPE_CODES = new Set([
      'SAMPLE_BALE', 'EMPTY', 'QUARTER_LOADED', 'HALF_LOADED',
      'FULL_LOADED', 'ONE_DOOR_CLOSED', 'BOTH_DOORS_CLOSED',
      'CLOSE_UP_SEAL', 'EXTRA',
    ]);

    // photoTypeItems를 TaskPhotoTypeRequestItemDto[]로 변환 (커스텀 타입만)
    const photoTypeRequests: TaskPhotoTypeRequestItemDto[] = photoTypeItems
      .filter((item) => item.isUse && !LEGACY_TYPE_CODES.has(item.typeCode))
      .map((item) => ({
        typeCode: item.typeCode,
        count: item.count,
        remark: item.remark || undefined,
        displayOrder: item.displayOrder,
      }));

    // closeUpSeal과 extra는 고정 필드 (PhotoTypeCode에 없음) - form 값 직접 사용
    const closeUpSealValue = getValues("closeUpSeal");
    const extraValue = getValues("extra");
    const extraMediaRemark = extraValue?.isUse ? extraValue?.remark : undefined;

    const transformData = transformValueOrNull({
      targetObject: {
        workingDayAt,
        transportDateAt,
        transportRemark,
        containerTaskRequestRemark,
        bookingId,
        contractId,
        numberOfTaskContainer,
        mediaCoordinateRange,
        extraMediaRemark,
        workplaceId: Number(workplaceId),
        taskWorkplaceEmployeeInfos: taskWorkplaceEmployeeInfos
          .filter(({ workplaceEmployeeId }) => workplaceEmployeeId)
          .map((employee) => {
            return {
              ...employee,
              workplaceEmployeeId: Number(employee.workplaceEmployeeId),
            };
          }),
        // 새로운 동적 photo type 시스템
        photoTypeRequests,
      },
    });

    // 기존 API 호환용 - photoTypeItems에서 각 타입별 count 추출
    const sampleBale = getPhotoTypeByCode("SAMPLE_BALE" as PhotoTypeCode);
    const empty = getPhotoTypeByCode("EMPTY" as PhotoTypeCode);
    const quarterLoaded = getPhotoTypeByCode("QUARTER_LOADED" as PhotoTypeCode);
    const halfLoaded = getPhotoTypeByCode("HALF_LOADED" as PhotoTypeCode);
    const fullLoaded = getPhotoTypeByCode("FULL_LOADED" as PhotoTypeCode);
    const oneDoorClosed = getPhotoTypeByCode("ONE_DOOR_CLOSED" as PhotoTypeCode);
    const bothDoorsClosed = getPhotoTypeByCode(
      "BOTH_DOORS_CLOSED" as PhotoTypeCode
    );

    try {
      handleContentLoadingOn();
      await editLoading({
        id: Number(params.id),
        // 기존 API 호환용 필드들 (photoTypeItems에서 추출)
        containerSampleBaleMediasCount: zeroOrPositiveNumber(
          !!sampleBale?.isUse,
          Number(sampleBale?.count || 0)
        ),
        containerEmptyMediasCount: zeroOrPositiveNumber(
          !!empty?.isUse,
          Number(empty?.count || 0)
        ),
        containerQuarterLoadedMediasCount: zeroOrPositiveNumber(
          !!quarterLoaded?.isUse,
          Number(quarterLoaded?.count || 0)
        ),
        containerHalfLoadedMediasCount: zeroOrPositiveNumber(
          !!halfLoaded?.isUse,
          Number(halfLoaded?.count || 0)
        ),
        containerFullLoadedMediasCount: zeroOrPositiveNumber(
          !!fullLoaded?.isUse,
          Number(fullLoaded?.count || 0)
        ),
        containerOneDoorClosedMediasCount: zeroOrPositiveNumber(
          !!oneDoorClosed?.isUse,
          Number(oneDoorClosed?.count || 0)
        ),
        containerBothDoorsClosedMediasCount: zeroOrPositiveNumber(
          !!bothDoorsClosed?.isUse,
          Number(bothDoorsClosed?.count || 0)
        ),
        containerCloseUpSealMediasCount: Number(closeUpSealValue?.count || 1),
        containerExtraMediasCount: zeroOrPositiveNumber(
          !!extraValue?.isUse,
          Number(extraValue?.count || 0)
        ),
        workEstimatedMinute: !!workEstimatedTime
          ? dayjs(workEstimatedTime).hour() * 60 +
            dayjs(workEstimatedTime).minute()
          : null,
        ...(transformData as Omit<
          ModifyTaskDto,
          "containerCloseUpSealMediasCount"
        >),
      }).unwrap();

      alert.showAlert({
        type: "success",
        message: t("task:add.alert.saveSuccess"),
      });
      navigate(`${EXPORTER_PRIVATE_PATH.LOADING_DETAIL}/${params.id}`);
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    } finally {
      handleContentLoadingOff();
    }
  };

  // 부킹 정보
  const renderBookingSelection = () => {
    return (
      <HalfSectionCard
        cardTitle={
          <>
            <RequiredSectionCardHeader>
              {bookingDetail
                ? t("task:add.common.bookingInformation")
                : t("task:add.common.bookingSelection")}{" "}
              <strong>*</strong>
              {isSubmitted && !bookingDetail && (
                <div>
                  <InputError message={t("error:required")} />
                </div>
              )}
            </RequiredSectionCardHeader>
            {bookingDetail && (
              <ChooseAgainButton
                buttonColor="black"
                buttonGrade="tertiary"
                buttonSize={32}
                onClick={() => setDialogState(DialogState.BOOKING)}
              >
                {t("task:add.buttonLabel.chooseAgain")}
              </ChooseAgainButton>
            )}
          </>
        }
      >
        {bookingDetail ? (
          <>
            <InformationBoardBox>
              <InformationItem>
                <Typo typoType="b9m" color="gray6">
                  {t("common:noOfContainer")}
                </Typo>
                <Typo typoType="h4" color="blue2">
                  {`${bookingDetail.numberOfContainer.toLocaleString(
                    "ko-KR"
                  )}x${bookingDetail.containerType}`}
                </Typo>
              </InformationItem>
              <InformationItem>
                <Typo typoType="b9m" color="gray6">
                  {t("task:add.common.remainingContainer")}
                </Typo>
                <Typo typoType="h4" color="blue2">
                  {`${bookingDetail.remainingNumberOfContainer.toLocaleString(
                    "ko-KR"
                  )} ${t("common:ea")}`}
                </Typo>
              </InformationItem>
            </InformationBoardBox>
            <BookingDetailContainer>
              {[
                {
                  label: t("common:bookingNo"),
                  value: (
                    <StyledTextButton
                      onClick={() =>
                        navigate(
                          `${EXPORTER_PRIVATE_PATH.BOOKING_DETAIL}/${bookingDetail.id}`
                        )
                      }
                    >
                      {bookingDetail.bookingNo}
                    </StyledTextButton>
                  ),
                },
                {
                  label: "ETD",
                  value: (
                    <>
                      {dayjs(bookingDetail.etdAt).format("YYYY-MM-DD")}{" "}
                      <EtdCompareCount
                        typoType="b7m"
                        initDate={bookingDetail.initialEtdAt}
                        currentDate={bookingDetail.etdAt}
                      />
                    </>
                  ),
                },

                {
                  label: t("common:cargoCutOff"),
                  value: dayjs(bookingDetail.cargoCutOffAt).format(
                    dayjs(bookingDetail.cargoCutOffAt).get("minute") === 30
                      ? "YYYY-MM-DD"
                      : "YYYY-MM-DD HH:mm"
                  ),
                },
                {
                  label: <PreWrap>{t("common:docCutOffWordBreak")}</PreWrap>,
                  value: dayjs(bookingDetail.docCutOffAt).format(
                    dayjs(bookingDetail.docCutOffAt).get("minute") === 30
                      ? "YYYY-MM-DD"
                      : "YYYY-MM-DD HH:mm"
                  ),
                },
                {
                  label: t("common:estimatedWeight"),
                  value:
                    bookingDetail.estimatedWeight &&
                    bookingDetail.estimatedWeightUnit ? (
                      <>
                        {Number(bookingDetail.estimatedWeight)?.toLocaleString(
                          "ko-KR"
                        )}{" "}
                        {bookingDetail.estimatedWeightUnit}
                      </>
                    ) : (
                      "-"
                    ),
                },
                { label: t("common:cfsCyCode"), value: bookingDetail.cfs },
              ].map(({ label, value }) => {
                return <SectionCardRow label={label} value={value} />;
              })}
            </BookingDetailContainer>
          </>
        ) : (
          <SelectionCardContent>
            <Typo typoType="b7r" color="gray6">
              {t("task:add.placeholder.bookingSelection")}
            </Typo>
            <Button
              ref={(node) => (buttonRef.current = node)}
              buttonGrade="secondary"
              buttonSize={40}
              onClick={() => setDialogState(DialogState.BOOKING)}
              disabled={!isEditAuth}
            >
              {t("task:add.buttonLabel.bookingSelection")}
            </Button>
          </SelectionCardContent>
        )}
      </HalfSectionCard>
    );
  };

  // 계약 정보
  const renderContractSelection = () => {
    return (
      <HalfSectionCard
        cardContentContainerStyle={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flex: 1,
        }}
        cardTitle={
          <>
            <RequiredSectionCardHeader>
              {contractDetail
                ? t("task:add.common.contractInformation")
                : t("task:add.common.contractSelection")}{" "}
              <strong>*</strong>
              {isSubmitted && !contractDetail && (
                <div>
                  <InputError message={t("error:required")} />
                </div>
              )}
            </RequiredSectionCardHeader>
            {contractDetail && (
              <ChooseAgainButton
                buttonColor="black"
                buttonGrade="tertiary"
                buttonSize={32}
                onClick={() => setDialogState(DialogState.CONTRACT)}
              >
                {t("task:add.buttonLabel.chooseAgain")}
              </ChooseAgainButton>
            )}
          </>
        }
      >
        {contractDetail ? (
          <>
            <InformationBoardBox>
              <InformationItem>
                <Typo typoType="b9m" color="gray6">
                  {t("common:buyer")}
                </Typo>
                <Typo typoType="h4" color="blue2">
                  {contractDetail.buyerNameCode}
                </Typo>
              </InformationItem>
              <InformationItem>
                <Typo typoType="b9m" color="gray6">
                  {t("common:item")}
                </Typo>
                <Typo typoType="h4" color="blue2">
                  {contractDetail.item}
                </Typo>
              </InformationItem>
            </InformationBoardBox>
            <BookingDetailContainer>
              {[
                {
                  label: t("common:itemCode"),
                  value: contractDetail.itemCode,
                },
                {
                  label: t("common:scNo"),
                  value: (
                    <StyledTextButton
                      onClick={() =>
                        navigate(
                          `${EXPORTER_PRIVATE_PATH.CONTRACT_DETAIL}/${contractDetail.id}`
                        )
                      }
                    >
                      {contractDetail.scNo}
                    </StyledTextButton>
                  ),
                },
                {
                  label: t("common:poNo"),
                  value: contractDetail.poNo ?? "-",
                },
                {
                  label: t("common:qty"),
                  value: `${Number(contractDetail.quantity).toLocaleString(
                    "ko-KR"
                  )} ${contractDetail.quantityUnit}`,
                },
                {
                  label: t("common:unitPrice"),
                  value: `${Number(contractDetail.unitPrice).toLocaleString(
                    "ko-KR"
                  )} ${contractDetail.unitPriceUnit}`,
                },
                {
                  label: t("common:shippingTerms"),
                  value: `${contractDetail.shippingTerms} ${
                    contractDetail.shippingTermsRemark ?? ""
                  }`,
                },
                {
                  label: t("common:placeOfDelivery"),
                  value:
                    contractDetail?.anyPortIn ??
                    contractDetail?.placeOfDelivery ??
                    "-",
                },
                {
                  label: t("common:allocation"),
                  value: contractDetail?.allocation ?? "-",
                  isHidden: !contractDetail?.allocation,
                },
                {
                  label: t("common:originLocation"),
                  value: contractDetail?.origin ?? "-",
                },
                {
                  label: t("common:lastShipmentDate"),
                  value: !isNull(contractDetail.lastShipmentDateAt)
                    ? dayjs(contractDetail.lastShipmentDateAt).format(
                        "YYYY-MM-DD"
                      )
                    : "-",
                },
              ].map(({ label, value, isHidden = false }) => {
                if (isHidden) return null;
                return <SectionCardRow label={label} value={value} />;
              })}
            </BookingDetailContainer>
          </>
        ) : (
          <SelectionCardContent>
            <Typo typoType="b7r" color="gray6">
              {t("task:add.placeholder.contractSelection")}
            </Typo>
            <Button
              buttonGrade="secondary"
              buttonSize={40}
              disabled={!bookingDetail}
              onClick={() => setDialogState(DialogState.CONTRACT)}
            >
              {t("task:add.buttonLabel.contractSelection")}
            </Button>
          </SelectionCardContent>
        )}
      </HalfSectionCard>
    );
  };

  // 작업 정보
  const renderLoadingInformation = () => {
    return (
      <SectionCard
        cardContentContainerStyle={cardContentContainerStyle}
        cardTitle={t("task:add.common.loadingInformation")}
      >
        <FormItemsContainer>
          <FormItemContainer>
            <StyledFormItem
              control={control as any}
              label={t("task:add.common.workingDay")}
              rules={{ required: true }}
              inputProps={{
                placeholder: "YYYY-MM-DD HH:mm",
                showTime: true,
                format: "YYYY-MM-DD HH:mm",
                minuteStep: 30,
                showNow: false,
                value: watch("workingDayAt")
                  ? dayjs(watch("workingDayAt"))
                  : undefined,
                changeOnBlur: true,
                onChange: (date: Dayjs | null) => {
                  if (!date) return;
                  const isThirtyOrZeroMin =
                    date.minute() === 0 || date.minute() === 30;

                  const format = `YYYY-MM-DD HH:mm:00`;
                  const dateValue = `YYYY-MM-DD HH:${
                    isThirtyOrZeroMin ? date.minute() : "00"
                  }:00`;

                  if (isSameAsWorkingDay) {
                    setValue(
                      "transportDateAt",
                      dayjs(date.format(dateValue), format).toISOString()
                    );
                  }

                  setValue(
                    "workingDayAt",
                    dayjs(date.format(dateValue), format).toISOString()
                  );
                },
              }}
              name="workingDayAt"
              type="date"
              errorsMessage={{
                required: t("error:required"),
              }}
            />
          </FormItemContainer>
          <FormItemContainer>
            <CustomField data-responsive>
              <FormKey>
                {t("common:noOfContainer")} <strong>*</strong>
              </FormKey>

              <CustomFieldContainer>
                <Controller
                  control={control}
                  name="numberOfTaskContainer"
                  rules={{ required: true, validate: (number) => number > 0 }}
                  render={({ field }) => {
                    return (
                      <HalfInputContainer>
                        <NumberInput
                          {...field}
                          disabled
                          data-invalid={!!errors.numberOfTaskContainer}
                        />
                        <Input
                          disabled
                          placeholder={bookingDetail?.containerType ?? ""}
                        />
                      </HalfInputContainer>
                    );
                  }}
                />

                {!!errors.numberOfTaskContainer && (
                  <InputError message={t("error:required")} />
                )}

                <WarningDescription>
                  <Icon iconSrc={InfoSvg} iconSize={16} />
                  <Typo typoType="b9r" color="gray6">
                    {getMaxContainerInfoViaLang(
                      t,
                      bookingDetail
                        ? bookingDetail.remainingNumberOfContainer > 30
                          ? 30
                          : bookingDetail.remainingNumberOfContainer
                        : 0
                    )}
                  </Typo>
                </WarningDescription>
              </CustomFieldContainer>
            </CustomField>
          </FormItemContainer>
          <FormItemContainer>
            <StyledFormItem
              type="timePicker"
              control={control as any}
              label={t("task:add.common.workEstimatedTime")}
              name="workEstimatedTime"
              inputProps={{
                value: watch("workEstimatedTime")
                  ? dayjs(watch("workEstimatedTime"), "HH:mm")
                  : undefined,
                placeholder: "HH:mm",
                format: "HH:mm",
                minuteStep: 30,
                showNow: false,
                changeOnBlur: true,
                suffixIcon: <TimePickerSuffixIcon $url={ClockSvg} />,
                onSelect: (e: any) => {
                  const isZeroHour = dayjs(e).get("hour") === 0;

                  if (isZeroHour) {
                    return setValue(
                      "workEstimatedTime",
                      dayjs().set("hour", 0).set("minute", 30)
                    );
                  }
                },
                disabledTime: () => {
                  return {
                    disabledMinutes: (selectHour: number) => {
                      if (selectHour === 0) {
                        return [0];
                      }

                      return [];
                    },
                  };
                },
              }}
            />
          </FormItemContainer>

          <FormItemContainer>
            <StyledFormItem
              control={control as any}
              label={t("common:estimatedWeight")}
              name=""
              inputProps={{
                disabled: true,
                placeholder: `${
                  bookingDetail
                    ? `${
                        isNaN(
                          parseFloat(
                            Number(
                              (bookingDetail?.estimatedWeight ?? NaN) /
                                bookingDetail.numberOfContainer
                            ).toFixed(3)
                          )
                        )
                          ? "-"
                          : (
                              parseFloat(
                                Number(
                                  (bookingDetail?.estimatedWeight ?? 0) /
                                    bookingDetail.numberOfContainer
                                ).toFixed(3)
                              ) * watch("numberOfTaskContainer")
                            ).toLocaleString("ko-KR")
                      } ${bookingDetail?.estimatedWeightUnit ?? ""}`
                    : "-"
                }`,
              }}
              type="text"
            />
          </FormItemContainer>
          <FormItemContainer>
            <StyledFormItem
              control={control as any}
              label={t("task:add.common.loadingCode")}
              name="taskCode"
              inputProps={{
                disabled: true,
                placeholder: watch("taskCode") ?? "-",
                value: undefined,
              }}
              type="text"
            />
          </FormItemContainer>
        </FormItemsContainer>
      </SectionCard>
    );
  };

  // Photo Information (동적 photo type 시스템)
  const renderPhotoInformation = () => {
    const photoTypeItems = watch("photoTypeItems") || [];

    // photoTypeItems 항목 업데이트 핸들러
    const updatePhotoTypeItem = (
      index: number,
      updates: Partial<PhotoTypeFormItem>
    ) => {
      const currentItems = getValues("photoTypeItems");
      const newItems = currentItems.map((item, i) =>
        i === index ? { ...item, ...updates } : item
      );
      setValue("photoTypeItems", newItems);
    };

    // Close-up Seal 업데이트 핸들러
    // TODO: 성능 최적화를 위해 useCallback으로 컴포넌트 레벨로 이동 고려
    const updateCloseUpSeal = (updates: Partial<{ count: number; isUse: boolean }>) => {
      const current = getValues("closeUpSeal") || { count: 1, isUse: true };
      setValue("closeUpSeal", { count: current.count, isUse: current.isUse, ...updates });
    };

    // Extra 업데이트 핸들러
    const updateExtra = (updates: Partial<{ count: number; isUse: boolean; remark?: string }>) => {
      const current = getValues("extra") || { count: 1, isUse: false, remark: "" };
      setValue("extra", { count: current.count, isUse: current.isUse, remark: current.remark, ...updates });
    };

    return (
      <SectionCard cardTitle={t("task:add.label.photoInformation")}>
        <Flex flexDirection="column" gap={24}>
          <Controller
            name="mediaCoordinateRange"
            control={control}
            rules={{ required: true }}
            render={() => (
              <Flex flexDirection="column" gap={8}>
                <Label required>
                  {t("task:add.label.mediaCoordinateRange")}
                </Label>
                <StyledAntdRadioGroup
                  value={watch("mediaCoordinateRange")}
                  onChange={(e) =>
                    setValue("mediaCoordinateRange", e.target.value)
                  }
                >
                  <Flex gap={16}>
                    {MEDIA_COORDINATE_RANGE_OPTION_LIST.map(
                      ({ label, value, mapSrc }) => {
                        return (
                          <MapScopeContainer
                            key={value}
                            flexDirection="column"
                            gap={8}
                          >
                            <Radio value={value} id="mediaCoordinateRange">
                              {t(`task:${label}`)}
                            </Radio>
                            <MapScope
                              $backgroundImage={mapSrc}
                              onClick={() =>
                                setValue("mediaCoordinateRange", value)
                              }
                            />
                          </MapScopeContainer>
                        );
                      }
                    )}
                  </Flex>
                </StyledAntdRadioGroup>

                <Flex alignItems="center" gap={6}>
                  <StyledIcon iconSrc={InfoGray6Svg} iconSize={16} />
                  <Typo typoType="b9r" color="gray6">
                    {t("task:add.notification.mediaCoordinateRange")}
                  </Typo>
                </Flex>
              </Flex>
            )}
          />

          <PhotoTypeFields
            photoTypeItems={photoTypeItems}
            closeUpSeal={watchedCloseUpSeal}
            extra={watchedExtra}
            isLoading={isPhotoTypeConfigLoading || !isTaskDataLoaded}
            onUpdatePhotoTypeItem={updatePhotoTypeItem}
            onUpdateCloseUpSeal={updateCloseUpSeal}
            onUpdateExtra={updateExtra}
            control={control}
            checkboxDisabled
          />
        </Flex>
      </SectionCard>
    );
  };

  // 작업 요청 전송
  const renderSendLoadingRequest = () => {
    return (
      <SectionCard
        cardTitle={t("common:sendLoadingRequest")}
        cardContentContainerStyle={cardContentContainerStyle}
      >
        <CustomField>
          <FormKey>
            {t("task:add.common.factory")} <strong>*</strong>
          </FormKey>

          <CustomFieldContainer>
            <Controller
              control={control}
              name="workplaceId"
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <HalfInputContainer>
                    <StyledSelect
                      {...field}
                      options={factoriesToOptionList}
                      disabled={
                        isFetching ||
                        !isEditAuth ||
                        isContractDetailFetching ||
                        isUndefined(watch("contractId"))
                      }
                      suffixIcon={
                        isFetching || isContractDetailFetching ? (
                          <Loader />
                        ) : undefined
                      }
                      placeholder={t("task:add.placeholder.factory")}
                      data-invalid={!!errors.workplaceId}
                      onChange={async (value) => {
                        try {
                          await getFactoryEmployees({ id: value });
                          setValue("workplaceId", value);
                          setValue(
                            "taskWorkplaceEmployeeInfos",
                            editTaskPageDefaultValue.taskWorkplaceEmployeeInfos
                          );
                        } catch (e: any) {
                          const message = Array.isArray(e.data.message)
                            ? e.data.message[0]
                            : e.data.message;
                          alert.showAlert({ type: "error", message });
                        }
                      }}
                    />
                    <Button
                      buttonColor="black"
                      buttonGrade="tertiary"
                      onClick={() =>
                        setDialogState(DialogState.ADD_NEW_CONTACT_PERSON)
                      }
                      disabled={!watch("workplaceId")}
                    >
                      {t("task:add.buttonLabel.addNewContactPerson")}
                    </Button>
                  </HalfInputContainer>
                );
              }}
            />

            {!!errors.workplaceId && (
              <InputError message={t("error:required")} />
            )}
          </CustomFieldContainer>
        </CustomField>

        <ManagersInfoContainer>
          <ManagerInfoContainer>
            <CustomField data-responsive>
              <FormKey>
                {t("task:add.common.manager")} 01 <strong>*</strong>
              </FormKey>
              <CustomFieldContainer>
                <Controller
                  control={control}
                  name="taskWorkplaceEmployeeInfos.0.workplaceEmployeeId"
                  rules={{ required: true }}
                  render={({ field }) => {
                    return (
                      <StyledSelect
                        {...field}
                        options={factoryEmployeesToOptionList.filter(
                          ({ value }) => {
                            return (
                              value !==
                              watch("taskWorkplaceEmployeeInfos.1")
                                .workplaceEmployeeId
                            );
                          }
                        )}
                        suffixIcon={
                          isFactoryEmployeesFetching ? <Loader /> : undefined
                        }
                        data-invalid={
                          !!errors?.taskWorkplaceEmployeeInfos?.[0]
                            ?.workplaceEmployeeId
                        }
                        placeholder={t("task:add.placeholder.manager")}
                        disabled={isFactoryEmployeesFetching}
                        allowClear
                        onChange={(value) => {
                          if (value) {
                            field.onChange(value);
                          } else {
                            setValue(
                              "taskWorkplaceEmployeeInfos.0",
                              !!watch("taskWorkplaceEmployeeInfos.1")
                                .workplaceEmployeeId
                                ? {
                                    workplaceEmployeeId: watch(
                                      "taskWorkplaceEmployeeInfos.1"
                                    ).workplaceEmployeeId,
                                    transmissionMethod:
                                      watch("taskWorkplaceEmployeeInfos.1")
                                        .transmissionMethod ?? "ALL",
                                  }
                                : {
                                    workplaceEmployeeId: undefined,
                                    transmissionMethod: "ALL",
                                  }
                            );
                            setValue("taskWorkplaceEmployeeInfos.1", {
                              workplaceEmployeeId: undefined,
                              transmissionMethod: "ALL",
                            });
                          }
                        }}
                      />
                    );
                  }}
                />

                {!!errors?.taskWorkplaceEmployeeInfos?.[0]
                  ?.workplaceEmployeeId && (
                  <InputError message={t("error:required")} />
                )}
              </CustomFieldContainer>
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.name")}</SubFormKey>
              <Input
                disabled
                placeholder={
                  factoryEmployees.find(
                    ({ id }) =>
                      id.toString() ===
                      watch("taskWorkplaceEmployeeInfos")?.[0]
                        .workplaceEmployeeId
                  )?.name ?? "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.contact")}</SubFormKey>
              <div style={{ width: "140px", flexShrink: 0 }}>
                <Input
                  disabled
                  placeholder={
                    watch("taskWorkplaceEmployeeInfos")?.[0].workplaceEmployeeId
                      ? `${
                          factoryEmployees.find(
                            ({ id }) =>
                              id.toString() ===
                              watch("taskWorkplaceEmployeeInfos")?.[0]
                                .workplaceEmployeeId
                          )?.personalContactPrefix
                        }`
                      : "-"
                  }
                />
              </div>
              <Input
                disabled
                placeholder={
                  watch("taskWorkplaceEmployeeInfos")?.[0].workplaceEmployeeId
                    ? `${
                        factoryEmployees.find(
                          ({ id }) =>
                            id.toString() ===
                            watch("taskWorkplaceEmployeeInfos")?.[0]
                              .workplaceEmployeeId
                        )?.personalContact
                      }`
                    : "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.email")}</SubFormKey>
              <Input
                disabled
                placeholder={
                  factoryEmployees.find(
                    ({ id }) =>
                      id.toString() ===
                      watch("taskWorkplaceEmployeeInfos")?.[0]
                        .workplaceEmployeeId
                  )?.email ?? "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.receive")}</SubFormKey>
              <Controller
                control={control}
                name="taskWorkplaceEmployeeInfos.0.transmissionMethod"
                render={({ field }) => {
                  return (
                    <StyledRadioGroup
                      {...field}
                      options={[
                        { label: t("common:all"), value: "ALL" },
                        { label: t("common:email.en"), value: "EMAIL" },
                        { label: t("common:sms.en"), value: "SMS" },
                      ]}
                    />
                  );
                }}
              />
            </CustomField>
          </ManagerInfoContainer>
          <ManagerInfoContainer>
            <CustomField data-responsive>
              <FormKey>{t("task:add.common.manager")} 02</FormKey>
              <CustomFieldContainer>
                <Controller
                  control={control}
                  name="taskWorkplaceEmployeeInfos.1.workplaceEmployeeId"
                  render={({ field }) => {
                    return (
                      <StyledSelect
                        {...field}
                        options={factoryEmployeesToOptionList.filter(
                          ({ value }) => {
                            return (
                              value !==
                              watch("taskWorkplaceEmployeeInfos.0")
                                .workplaceEmployeeId
                            );
                          }
                        )}
                        suffixIcon={
                          isFactoryEmployeesFetching ? <Loader /> : undefined
                        }
                        placeholder={t("task:add.placeholder.manager")}
                        disabled={isFactoryEmployeesFetching}
                        allowClear
                        onClear={() => {
                          setValue("taskWorkplaceEmployeeInfos.1", {
                            workplaceEmployeeId: undefined,
                            transmissionMethod: "ALL",
                          });
                        }}
                      />
                    );
                  }}
                />
              </CustomFieldContainer>
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.name")}</SubFormKey>
              <Input
                disabled
                placeholder={
                  factoryEmployees.find(
                    ({ id }) =>
                      id.toString() ===
                      watch("taskWorkplaceEmployeeInfos")?.[1]
                        .workplaceEmployeeId
                  )?.name ?? "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.contact")}</SubFormKey>
              <div style={{ width: "140px", flexShrink: 0 }}>
                <Input
                  disabled
                  placeholder={
                    watch("taskWorkplaceEmployeeInfos")?.[1].workplaceEmployeeId
                      ? `${
                          factoryEmployees.find(
                            ({ id }) =>
                              id.toString() ===
                              watch("taskWorkplaceEmployeeInfos")?.[1]
                                .workplaceEmployeeId
                          )?.personalContactPrefix
                        }`
                      : "-"
                  }
                />
              </div>
              <Input
                disabled
                placeholder={
                  watch("taskWorkplaceEmployeeInfos")?.[1].workplaceEmployeeId
                    ? `${
                        factoryEmployees.find(
                          ({ id }) =>
                            id.toString() ===
                            watch("taskWorkplaceEmployeeInfos")?.[1]
                              .workplaceEmployeeId
                        )?.personalContact
                      }`
                    : "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.email")}</SubFormKey>
              <Input
                disabled
                placeholder={
                  factoryEmployees.find(
                    ({ id }) =>
                      id.toString() ===
                      watch("taskWorkplaceEmployeeInfos")?.[1]
                        .workplaceEmployeeId
                  )?.email ?? "-"
                }
              />
            </CustomField>
            <CustomField>
              <SubFormKey>{t("task:add.common.receive")}</SubFormKey>
              <Controller
                control={control}
                name="taskWorkplaceEmployeeInfos.1.transmissionMethod"
                render={({ field }) => {
                  return (
                    <StyledRadioGroup
                      {...field}
                      options={[
                        { label: t("common:all"), value: "ALL" },
                        { label: t("common:email.en"), value: "EMAIL" },
                        { label: t("common:sms.en"), value: "SMS" },
                      ]}
                    />
                  );
                }}
              />
            </CustomField>
          </ManagerInfoContainer>
        </ManagersInfoContainer>
      </SectionCard>
    );
  };

  // 운송 정보
  const renderTransportInformation = () => {
    return (
      <HalfSectionCard
        cardTitle={t("task:add.common.transportInformation")}
        cardContentContainerStyle={cardContentContainerStyle}
      >
        <StyledFormItem
          label={t("task:add.common.transportDate")}
          type="date"
          name="transportDateAt"
          rules={{ required: true }}
          control={control as any}
          errorsMessage={{
            required: t("error:required"),
          }}
          inputProps={{
            showTime: true,
            showNow: false,
            changeOnBlur: true,
            placeholder: "YYYY-MM-DD HH:mm",
            format: "YYYY-MM-DD HH:mm",
            minuteStep: 10,
            disabled: isSameAsWorkingDay,
            value: watch("transportDateAt")
              ? dayjs(watch("transportDateAt"))
              : undefined,
            onChange: (date: Dayjs | null) => {
              if (!date) return null;
              const min = Math.floor(date.minute() / 10);
              const transformedMin = min * 10;

              setValue(
                "transportDateAt",
                dayjs(
                  date.format(`YYYY-MM-DD HH:${transformedMin}:00`),
                  `YYYY-MM-DD HH:mm:00`
                ).toISOString()
              );
            },
          }}
          bottomAccessory={
            <StyledLabel>
              <Checkbox
                checked={isSameAsWorkingDay}
                disabled={!watch("workingDayAt")}
                onChange={(val) => {
                  setIsSameAsWorkingDay((prev) => !prev);
                  const isChecked = val.target.checked;
                  if (isChecked) {
                    setValue("transportDateAt", watch("workingDayAt"));
                    clearErrors("transportDateAt");
                  }
                }}
              />
              <Typo typoType="b7r">
                {t("task:add.common.sameAsWorkingDay")}
              </Typo>
            </StyledLabel>
          }
        />
        <StyledFormItem
          label={t("task:add.common.transportRemark")}
          type="textarea"
          name="transportRemark"
          control={control as any}
          inputProps={{
            maxLength: 500,
            placeholder: t("task:add.placeholder.transportRemark"),
          }}
        />
      </HalfSectionCard>
    );
  };

  const renderContainerLoadingRequestRemark = () => {
    return (
      <HalfSectionCard cardTitle={t("task:add.common.loadingRequestRemark")}>
        <FormItem
          type="textarea"
          name="containerTaskRequestRemark"
          control={control}
          inputProps={{
            style: { width: "100%" },
            placeholder: t("task:add.placeholder.loadingRequestRemark"),
            maxLength: 1000,
          }}
          noLabel
        />
      </HalfSectionCard>
    );
  };

  const renderDialog = () => {
    if (dialogState === DialogState.NULL) return null;

    if (dialogState === DialogState.BOOKING)
      return (
        <LoadBookingDialog
          open
          onClose={() => setDialogState(DialogState.NULL)}
          onSelect={(detail) => {
            if (detail) {
              setValue("bookingId", detail.id);
              if (contractDetail) {
                setContractDetail(null);
              }
              setBookingDetail(detail);

              /** 기존 선택한 부킹과 새로 선택한 부킹이 다를 때만 */
              if (bookingDetail && bookingDetail.id !== detail.id) {
                const candidate = editTaskPageDefaultValue;
                /** 계약 초기화 */
                setContractDetail(null);
                setValue("contractId", candidate["contractId"]);
                /** 작업지와 작업 담당자 초기화 */
                setValue("workplaceId", candidate["workplaceId"]);
                setValue(
                  "taskWorkplaceEmployeeInfos",
                  candidate["taskWorkplaceEmployeeInfos"]
                );
              }
            }
          }}
          defaultSelectedItemId={bookingDetail?.id}
          maxContainer={watch().numberOfTaskContainer}
        />
      );
    if (dialogState === DialogState.CONTRACT) {
      if (!bookingDetail) return null;
      return (
        <LoadContractDialog
          open
          onClose={() => setDialogState(DialogState.NULL)}
          onSelect={async (detail) => {
            try {
              if (detail) {
                setValue("contractId", detail.id);
                setContractDetail(detail);

                await getBuyerDetail({
                  id: detail.buyerId,
                }).unwrap();
              }
            } catch (e) {
            } finally {
              /** 기존 선택한 계약과 새로 선택한 계약이 다를 때만 */
              if (detail && contractDetail && detail.id !== contractDetail.id) {
                const candidate = editTaskPageDefaultValue;
                /** 작업지와 작업 담당자 초기화 */
                setValue("workplaceId", candidate["workplaceId"]);
                setValue(
                  "taskWorkplaceEmployeeInfos",
                  candidate["taskWorkplaceEmployeeInfos"]
                );
              }
            }
          }}
          bookingId={bookingDetail.id}
          defaultSelectedItemId={contractDetail?.id}
        />
      );
    }

    if (dialogState === DialogState.ADD_NEW_CONTACT_PERSON) {
      return (
        <AddNewContactPersonDialog
          onClose={() => setDialogState(DialogState.NULL)}
          factoryId={Number(watch("workplaceId"))}
          factoryName={
            factoryList.find(
              ({ workplaceListQueryResultDto }) =>
                workplaceListQueryResultDto.id.toString() ===
                watch("workplaceId")
            )?.workplaceName ?? ""
          }
          defaultCountryCodeItemName={
            factoryList.find(
              ({ workplaceListQueryResultDto }) =>
                workplaceListQueryResultDto.id.toString() ===
                watch("workplaceId")
            )?.workplaceListQueryResultDto.countryCodeItemName ?? ""
          }
          defaultCountryCodePrefix={
            factoryList.find(
              ({ workplaceListQueryResultDto }) =>
                workplaceListQueryResultDto.id.toString() ===
                watch("workplaceId")
            )?.workplaceListQueryResultDto.telPrefix ?? ""
          }
        />
      );
    }
    return null;
  };

  const renderAlertDialog = () => {
    if (alertDialogState === AlertDialogState.NULL) return null;

    if (alertDialogState === AlertDialogState.BACK_TO_PREVIOUS) {
      return (
        <AlertDialog
          open
          title={t("task:add.alertDialog.backToPrevious.title")}
          onOpenChange={() => {
            setAlertDialogState(AlertDialogState.NULL);
          }}
          onOk={() => navigate(-1)}
          okText={t("task:add.common.ok")}
          cancelText={t("task:add.common.exit")}
        >
          {t("task:add.alertDialog.backToPrevious.description")}
        </AlertDialog>
      );
    }
  };

  const renderUnauthorizedDescription = () => {
    if (isLoadingUnstable || isItemCodeUnstable) {
      return;
    }

    return (
      <NoDataContainer>
        <UnauthorizedDescription />
      </NoDataContainer>
    );
  };

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        handleContentLoadingOn();
        const {
          contractId,
          bookingId,
          simpleTaskWorkplaceEmployees,
          workingDayAt,
          numberOfTaskContainer,
          taskCode,
          workplaceId,
          transportDateAt,
          transportRemark,
          containerTaskRequestRemark,
          extraMediaRemark,
          simpleTaskMemos,
          buyerId,
          mediaCoordinateRange,
          photoTypeRequests, // 동적 photo type 시스템
          ...restResponse
        } = await getLoadingDetail({
          id: Number(params.id),
        }).unwrap();

        setLoadedPhotoTypeRequests(
          (photoTypeRequests as TaskPhotoTypeRequestResponseDto[]) || []
        );

        await getBuyerDetail({ id: buyerId });

        const bookingListResponse = await getBookingList({ bookingId });
        if (bookingListResponse.data) {
          setBookingDetail(
            bookingListResponse.data.rows.find(
              ({ id }) => id === bookingId
            ) as TaskBookingListViewDto
          );
        }
        const contractListResponse = await getContractList({ id: bookingId });
        if (contractListResponse.data) {
          setContractDetail(
            contractListResponse.data.rows.find(
              ({ id }) => id === contractId
            ) as BookingContractListViewDto
          );
        }

        await getFactoryEmployees({ id: workplaceId });

        const loadingWorkplaceEmployee = [
          ...simpleTaskWorkplaceEmployees,
          {
            transmissionMethod: "ALL",
            workplaceEmployeeId: undefined,
          },
          {
            transmissionMethod: "ALL",
            workplaceEmployeeId: undefined,
          },
        ]
          .map(({ transmissionMethod, workplaceEmployeeId }) => ({
            transmissionMethod,
            workplaceEmployeeId: workplaceEmployeeId?.toString() ?? undefined,
          }))
          .filter((_, idx) => idx < 2);

        const transformContainerCount = Object.entries(
          photoTypeMap
        ).reduce<any>((acc, [key, formKey]) => {
          const numberOfPhotoType = restResponse[key as PhotoTypeContainerKey];

          let resource: Record<
            string,
            {
              count: number;
              isUse: boolean;
              remark?: string | null;
            }
          > = {
            [formKey]: {
              count: numberOfPhotoType === 0 ? 1 : numberOfPhotoType,
              isUse: numberOfPhotoType !== 0,
            },
          };

          if (key === "containerExtraMediasCount") {
            resource = {
              [formKey]: {
                ...resource[formKey],
                remark: numberOfPhotoType === 0 ? "" : extraMediaRemark,
              },
            };
          }

          return { ...acc, ...resource };
        }, {});
        setExcludedManagerId(
          loadingWorkplaceEmployee.map(({ workplaceEmployeeId }) =>
            workplaceEmployeeId?.toString()
          )
        );
        reset({
          bookingId,
          contractId,
          workplaceId: workplaceId.toString(),
          workingDayAt,
          workEstimatedTime:
            isNull(restResponse.workEstimatedMinute) ||
            isUndefined(restResponse.workEstimatedMinute)
              ? undefined
              : dayjs()
                  .startOf("day")
                  .add(
                    Math.floor(restResponse.workEstimatedMinute / 60),
                    "hour"
                  )
                  .add(restResponse.workEstimatedMinute % 60, "minutes"),
          numberOfTaskContainer,
          taskCode,
          transportDateAt,
          taskWorkplaceEmployeeInfos: loadingWorkplaceEmployee,
          containerTaskRequestRemark,
          transportRemark,
          mediaCoordinateRange,
          photoTypeItems: [], // 별도 useEffect에서 설정
          ...(transformContainerCount as any),
        });
        // Task 데이터 로딩 완료 표시
        setIsTaskDataLoaded(true);
      } catch (e: any) {
        const error = e as ErrorResponse;
        const message = Array.isArray(error.data.message)
          ? e.data.message[0]
          : e.data.message;

        if (error.status === 404) {
          return;
        } else {
          alert.showAlert({ type: "error", message });
        }
      } finally {
        handleContentLoadingOff();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      await getItemCodes({ itemCode: undefined });
    })();
  }, [getItemCodes]);

  // 계약 상세조회
  useEffect(() => {
    if (!contractDetail) {
      return;
    }
    (async () => {
      try {
        await getContractDetail({ id: contractDetail.id });
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;
        alert.showAlert({ type: "error", message });
      }
    })();
  }, [alert, contractDetail, getContractDetail]);

  useEffect(() => {
    if (!isTaskDataLoaded || !photoTypeConfig || isPhotoTypeConfigLoading) {
      return;
    }

    const mergedPhotoTypeItems: PhotoTypeFormItem[] = (loadedPhotoTypeRequests || []).map(
      (req) => ({
        typeCode: req.typeCode,
        title: req.title,
        subTitle: req.subTitle ?? null,
        count: req.count,
        remark: req.remark || "",
        displayOrder: req.displayOrder,
        isUse: true,
        minCount: req.count,
      })
    );

    const sortedPhotoTypeItems = [...mergedPhotoTypeItems].sort(
      (a, b) => a.displayOrder - b.displayOrder
    );

    setValue("photoTypeItems", sortedPhotoTypeItems);
  }, [
    isTaskDataLoaded,
    photoTypeConfig,
    isPhotoTypeConfigLoading,
    loadedPhotoTypeRequests,
    setValue,
  ]);

  return (
    <ExporterMainLayout
      breadcrumb={
        !isLoadingFetchingError && isMainCategoryAuthorized
          ? [t("sideNav:loading"), t("sideNav:loadingEdit")]
          : []
      }
      pageTitle={
        !isLoadingFetchingError &&
        isMainCategoryAuthorized &&
        t("sideNav:loadingEdit")
      }
    >
      {isMainCategoryUnauthorized || isLoadingFetchingError ? (
        renderUnauthorizedDescription()
      ) : (
        <form onSubmit={handleSubmit(handleEditLoading)}>
          <FlexGapSection>
            <FlexHalfContainer>
              {renderBookingSelection()}
              {renderContractSelection()}
            </FlexHalfContainer>
            {renderLoadingInformation()}
            {renderPhotoInformation()}
            {renderSendLoadingRequest()}
            <FlexHalfContainer>
              {renderTransportInformation()}
              {renderContainerLoadingRequestRemark()}
            </FlexHalfContainer>
          </FlexGapSection>

          <BottomFixedContainer>
            <FooterButtonSection>
              <Button
                buttonGrade="tertiary"
                buttonColor="black"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
                onClick={() =>
                  setAlertDialogState(AlertDialogState.BACK_TO_PREVIOUS)
                }
              >
                <Icon iconSrc={ChevronLeftSvg} iconSize={16} />
                {t("task:add.buttonLabel.backToPrevious")}
              </Button>

              <SaveButtonContainer>
                <Button
                  buttonGrade="primary"
                  style={{ width: 158 }}
                  type="submit"
                  disabled={!isEditAuth}
                >
                  {t("task:add.buttonLabel.save")}
                </Button>
              </SaveButtonContainer>
            </FooterButtonSection>
          </BottomFixedContainer>
        </form>
      )}

      {renderDialog()}
      {renderAlertDialog()}
    </ExporterMainLayout>
  );
};

export default TaskEditPage;

const FlexHalfContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const HalfSectionCard = styled(SectionCard)`
  width: calc(50% - 8px);
  display: flex;
  flex-direction: column;
`;

const RequiredSectionCardHeader = styled.span`
  ${typo.h6};
  display: flex;
  align-items: center;
  gap: 4px;

  strong {
    color: ${colorSet.red2};
  }
`;

const FlexCenterDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FlexColumnCenterDirection = styled(FlexCenterDiv)`
  flex-direction: column;
`;

const SelectionCardContent = styled(FlexColumnCenterDirection)`
  gap: 16px;
  text-align: center;
`;

const FlexGapSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
`;

const FormItemContainer = styled.div`
  width: calc(50% - 12px);
`;

const FormItemsContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const CustomField = styled.div`
  display: flex;
  gap: 8px;

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    &[data-responsive="true"] {
      flex-direction: column;
    }
  }
`;

const FormKey = styled.p`
  ${typo.b7m};
  color: ${colorSet.gray2};
  width: 164px;
  flex-shrink: 0;

  strong {
    color: ${colorSet.red2};
  }
`;

const SubFormKey = styled(FormKey)`
  ${typo.b9r};
  color: ${colorSet.gray6};
`;

const HalfInputContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
`;

const CustomFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const WarningDescription = styled.p`
  ${typo.b9r};
  color: ${colorSet.gray4};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledSelect = styled(Select)`
  flex: 1;
`;

const ManagersInfoContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 24px;
`;

const ManagerInfoContainer = styled.div`
  width: calc(50% - 12px);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  gap: 6px;
`;

const StyledFormItem = styled(FormItem)`
  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    flex-direction: column;
    gap: 8px;
  }
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const FooterButtonSection = styled.section`
  display: flex;
  justify-content: space-between;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const BookingDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreWrap = styled.span`
  white-space: pre-wrap;
`;

const StyledTextButton = styled(TextButton)`
  color: ${colorSet.blue4};
  ${typo.h7}
`;

const ChooseAgainButton = styled(Button)`
  margin-left: auto;
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  cursor: pointer;
`;

const InformationBoardBox = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid orange;
  border-radius: 8px;
  border: 1px solid ${colorSet.gray10};
  background: ${colorSet.gray11};
  margin-bottom: 24px;
`;

const InformationItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  word-break: break-word;
  text-align: center;
`;

const TimePickerSuffixIcon = styled.span<{ $url: string }>`
  display: inline-block;
  width: 20px;
  height: 20px;

  ${({ $url }) => css`
    background: url(${$url});
  `};
`;

const NoDataContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledAntdRadioGroup = styled(AntdRadio.Group)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MapScopeContainer = styled(Flex)`
  width: 100%;
  max-width: 512px;
`;

const MapScope = styled.div<{ $backgroundImage: string }>`
  width: 100%;
  max-width: 512px;
  height: 200px;
  background: ${({ $backgroundImage }) => `url(${$backgroundImage})`} no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  border: 1px solid ${colorSet.gray9};
  cursor: pointer;
  position: relative;
  overflow: hidden;

  ${mediaQuery.FORM_INPUT_TO_FULL_INPUT} {
    width: 100%;
    height: 200px;
  }
`;
